import {
  Box,
  Button,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDropDown, ArrowRight, Circle, Delete, Edit, MoreVert } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import { showErrorSnackbar, showSuccessSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { useNavigate } from 'react-router-dom';
import { API, graphqlOperation } from 'aws-amplify';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import VirtualTableRow from '../../../components/DashboardComponents/VirtualTableRow.jsx';
import DashboardSearch from '../../../components/DashboardComponents/DashboardSearch.jsx';
import { useDeleteAccountMasterMutation } from '../../../redux/apis/accountMaster.api.js';
import { useDeleteAccountGroupMasterMutation } from '../../../redux/apis/accountGroupMaster.api.js';
import { dashboardApi } from '../../../redux/apis/dashboardApi.js';
import { settingsClose } from '../../../redux/reducers/companySkipSave.js';
import { getPartyMasterCustom, getPartyMasterAccountWiseCustom } from '../../../graphql';
import AddNewPartyModal from '../../PrimeFigma/Modals/AddNewPartyModal.jsx';
import { useThemes } from '../../../components/Theme/theme.js';
import { MoneyFormatv3 } from '../../../library/MoneyFormat.jsx';
import CreateAndEditGroupModal from '../../../components/modals/CreateAndEditGroupModal';
import AddBalanceSheetReceipt from '../../../components/modals/AddBalanceSheetReceipt.jsx';
import AddBalanceSheetPayment from '../../../components/modals/AddBalanceSheetPayment.jsx';
import AddPNFDebitNote from '../../../components/modals/AddPNFDebitNote.jsx';
import AddPNFJournal from '../../../components/modals/AddPNFJournal.jsx';
import AddPNFCreditNote from '../../../components/modals/AddPNFCreditNote.jsx';
import AddPNFPurchase from '../../../components/modals/AddPNFPurchase.jsx';
import AddPNFSales from '../../../components/modals/AddPNFSales.jsx';
import DeleteLedgerGroup from '../../../components/modals/DeleteLedgerGroup.jsx';
import CreateAndEditLedger from '../../../components/modals/CreateAndEditLedger.jsx';
import { flattenTree, handleFreeze, searchInAccountList } from '../../../utils/common.js';
import ActionButtons from '../../../components/Buttons/ActionButtons.jsx';
import { LedgerModal, useLedgerModalHandle } from '../../../features/AccountsAndGroups';

const commonStyle = {
  rowHeight: 45,
  fontSize: 15,
};

const buttonStyle = {
  borderRadius: 10,
  color: '#297179',
  backgroundColor: '#FFF',
  fontFamily: 'overpass',
  fontWeight: '700',
  fontSize: 15,
  paddingTop: 0.5,
  paddingBottom: 0.5,
  PaddingLeft: 1,
  paddingRight: 1,
  marginBottom: 1,
};

function TrailBalanceTable({
  totalDebit,
  totalCredit,
  rows,
  openingBalanceDiff,
  expandButtonClicked,
  hideAmount,
  hideZeroLedger,
  newLedgerAccountID,
  setAccountID,
  setNewLedgerResponse,
  refetchDb,
}) {
  const { theme } = useThemes();

  // Ref

  const parentRef = useRef();

  // =============
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /// ============

  // ===== States ===== ///

  const [searchText, setSearchText] = useState('');
  const [sortby, setSortBy] = useState('all');
  const [data, setData] = useState(rows);
  const [expandedRows, setExpandedRows] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [partyDetails, setPartyDetails] = useState(null);
  const [deleteId, setdeleteId] = useState();
  const [selectedRow, setSelectedRow] = useState(null);
  const [newPartyID, setNewPartyID] = useState(null);
  const [openModal, setOpenModal] = useState({
    groupModal: false,
    ledgerModal: false,
    editModal: false,
    partyModal: false,
    editGroupModal: false,
    deleteModal: false,
    transcationModal: false,
    receiptModal: false,
    journalModal: false,
    paymentModal: false,
    debitNoteModal: false,
    creditNoteModal: false,
    salesModal: false,
    purchaseModal: false,
  });

  const { saveSkipStatus } = useSelector((state) => state.companySipSaveReducer);

  // ==== Redux query ======/

  const [deleteAccountGroupMaster] = useDeleteAccountGroupMasterMutation();
  const [deleteAccountMaster] = useDeleteAccountMasterMutation();

  useEffect(() => {
    let filteredRows = [...rows];

    // Apply search & sort filter
    if ((searchText && sortby) || sortby !== 'all') {
      let searchResult = searchInAccountList(filteredRows, searchText, sortby);
      filteredRows = flattenTree(searchResult);
    }

    // Apply hide zero ledger filter
    if (hideZeroLedger) {
      filteredRows = filteredRows.filter((item) => item.category === 'group' || item?.balance !== 0);
    }

    setData(filteredRows);

    //  expanded groups
    const expanded = filteredRows.filter((row) => row.category === 'group').map((row) => ({ id: row.id, isExpand: true }));

    setExpandedRows(expanded);
  }, [rows, hideZeroLedger, searchText, sortby]);

  useEffect(() => {
    if (deleteId !== '') {
      deleteLedgers();
      setdeleteId('');
    }
  }, [deleteId]);

  useEffect(() => {
    if (newPartyID) findNewPartyAccountId();
  }, [newPartyID]);

  useEffect(() => {
    if (rows?.length > 0) {
      if (expandButtonClicked) {
        handleExpandAll();
      } else {
        handleCollapseAll();
      }
    }
  }, [expandButtonClicked]);

  const memorizeData = useMemo(() => data, [data]);

  const Freeze = useSelector((state) => state.userPrefrenceandReducer?.financialYear?.freezestatus);

  // Virtualizer setup
  // const rowVirtualizer = useWindowVirtualizer({
  //   count: memorizeData.length,
  //   // getScrollElement: () => parentRef.current,
  //   estimateSize: () => 45,
  //   overscan: 10,
  // });

  const rowVirtualizer = useWindowVirtualizer({
    count: memorizeData.length,
    estimateSize: () => 45,
    overscan: 15,
  });

  // functionalities

  const toggleExpandRow = useCallback(
    (id, type) => {
      setData((prevRows) => {
        const isRowExpanded = expandedRows.find((ele) => ele.id === id)?.isExpand || false;
        let updatedRows = [...prevRows];

        if (isRowExpanded) {
          // Collapse Logic
          const collapseIDs = data
            .filter((item) => item.category === 'group' && item.type === type && item.path?.split('/').includes(`${id}`))
            .map((item) => item.id);

          updatedRows = prevRows.filter(
            (item) => !item.path?.split('/').includes(`${id}`) || (item.id === id && item.category === 'group' && item.type === type)
          );

          setExpandedRows((prev) => prev.map((row) => (collapseIDs.includes(row.id) ? { ...row, isExpand: false } : row)));
        } else {
          // Expand Logic
          const forFilter = hideZeroLedger
            ? rows.filter((item) => (item.category === 'group' && item.type === type) || item.balance !== 0)
            : rows;

          const filteredData = forFilter.filter((item) => item.type === type && item.parent === id);

          const index = prevRows.findIndex((item) => item.type === type && item.category === 'group' && item.id === id);

          if (index !== -1) {
            updatedRows.splice(index + 1, 0, ...filteredData);
          } else {
            updatedRows = [...prevRows, ...filteredData];
          }

          setExpandedRows((prev) => prev.map((row) => (row.id === id ? { ...row, isExpand: true } : row)));
        }

        return updatedRows;
      });
    },
    [rows, expandedRows, data, hideZeroLedger]
  );

  const { openLedgerModal, closeLedgerModal, ledgerModalParams } = useLedgerModalHandle();

  const handleSelectRow = useCallback((row) => {
    setSelectedRow(row);

    if (Freeze === 1) return handleFreeze();

    const { modal, category, action, id, parent, name } = row || {};

    if (modal === 'ledger') {
      openLedgerModal({
        open: true,
        groupId: category === 'group' ? id : parent,
        accountId: action === 'edit' ? id : '',
        title: action === 'edit' ? name : '',
        restrictedflag: row?.restrictedflag,
      });
    } else {
      handleOpenModal(action === 'delete' ? 'deleteModal' : 'groupModal');
    }
  }, []);

  const handleOpenModal = (key) => {
    if (key in openModal) {
      setOpenModal((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const handleLedgerModal = ({ accountsId }) => {
    setNewLedgerResponse(selectedRow?.action === 'create' ? accountsId + 'ledger' : null);
    setSelectedRow(null);
  };

  async function deleteLedgers() {
    if (deleteId.category === 'group') {
      try {
        const response = await deleteAccountGroupMaster({
          accountgroupid: deleteId?.id,
        });

        if (response.data !== null) {
          // setBackdropOpen(false);
        }

        if (response?.data?.statuscode === 'Error') {
          showErrorSnackbar(response?.data?.statusmessage);
        } else {
          // setBackdropOpen(false);
          showSuccessSnackbar('Group Deleted');
        }
      } catch (error) {
        console.log(error);
        // setBackdropOpen(false);
      }
    }
    if (deleteId.category === 'ledger') {
      try {
        const response = await deleteAccountMaster({
          accountsid: deleteId?.id,
        });
        let { statuscode, statusmessage } = response.data;

        if (statuscode === 'Error') {
          if (statusmessage.includes('Transactions Already Recorded')) {
            showWarningSnackbar('Cannot delete ledger account : Transactions exists in the current year');
          } else {
            showWarningSnackbar(statusmessage);
          }
        } else {
          showSuccessSnackbar('Ledger Deleted');
        }
      } catch (error) {
        console.log(error);
      }
    }
    dispatch(dashboardApi.util.invalidateTags(['dashboard']));
  }

  async function findNewPartyAccountId() {
    try {
      const response = await API.graphql(
        graphqlOperation(getPartyMasterCustom, {
          partyid: newPartyID,
        })
      );
      if (selectedRow?.action === 'create') {
        setNewLedgerResponse([response?.data?.getPartyMaster[0]?.accountsid + 'ledger']);
      } else {
        setNewLedgerResponse([]);
      }
      refetchDb && refetchDb();
      setNewPartyID(null);
    } catch (error) {
      console.error('Error fetching new party account data:', error);
    }
  }

  const handleExpandAll = () => {
    const filteredData = hideZeroLedger ? rows.filter((item) => item.category === 'group' || item?.balance !== 0) : rows;
    setExpandedRows(filteredData.filter((row) => row.category === 'group').map((row) => ({ id: row.id, isExpand: true })));
    setData(filteredData);
  };

  const handleCollapseAll = () => {
    setExpandedRows(rows.filter((row) => row.category === 'group').map((row) => ({ id: row.id, isExpand: false })));
    const collapseData = data.filter((item) => item.category === 'group' && [1, 10, 18, 22, 20, 23, 24, 25, 26, 27, 28].includes(item.id));
    setData(collapseData);
  };

  async function deleteLedgers() {
    if (deleteId.category === 'group') {
      try {
        const response = await deleteAccountGroupMaster({
          accountgroupid: deleteId?.id,
        });

        if (response.data !== null) {
          // setBackdropOpen(false);
        }

        if (response?.data?.statuscode === 'Error') {
          showErrorSnackbar(response?.data?.statusmessage);
        } else {
          // setBackdropOpen(false);
          showSuccessSnackbar('Group Deleted');
        }
      } catch (error) {
        console.log(error);
        // setBackdropOpen(false);
      }
    }
    if (deleteId.category === 'ledger') {
      try {
        const response = await deleteAccountMaster({
          accountsid: deleteId?.id,
        });
        let { statuscode, statusmessage } = response.data;

        if (statuscode && statuscode === 'Error') {
          showErrorSnackbar(statusmessage);
        } else {
          showSuccessSnackbar('Ledger Deleted');
        }
      } catch (error) {
        console.log(error);
      }
    }
    dispatch(dashboardApi.util.invalidateTags(['dashboard']));
  }

  const handleTransactionModal = useCallback((category, id) => {
    if (category === 'ledger') {
      setAccountID(id);
    }
  }, []);

  const borderRightColor = theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#eef0f0';

  const currentTheme = theme?.palette?.TmId.id.toString().includes('9') ? 'black' : 'white';

  return (
    <>
      <Box
        sx={{
          boxSizing: 'border-box',
          minWidth: { xs: '700px', md: '100%' },
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              width: { xs: '40%', md: '60%' },
              backgroundColor: `${theme.palette.primary.main}  !important`,
            }}
            flexGrow={1}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRight: '4px solid ',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
              }}
            >
              <Box>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 'bold',
                    pl: { sm: 1, md: 4 },
                    color: '#FFF',
                    fontSize: { xs: '14px', lg: '16px' },
                    paddingLeft: { xs: '8px', sm: '0px' },
                  }}
                >
                  Trial Balance
                </Typography>
              </Box>
              <Box>
                <DashboardSearch searchText={searchText} setSearchText={setSearchText} sortby={sortby} setSortBy={setSortBy} />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: { xs: '30%', md: '20%' },
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              backgroundColor: `${theme.palette.primary.main}  !important`,
            }}
            flexGrow={1}
          >
            <Box
              sx={{
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
                height: '100%',
                borderRight: '4px solid ',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 'bold',
                  color: '#FFF',
                  fontSize: { xs: '14px', lg: '16px' },
                }}
              >
                Debit
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: { xs: '30%', md: '20%' },
              borderTopLeftRadius: '10px',
              borderTopRightRadius: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: `${theme.palette.primary.main}  !important`,
            }}
            flexGrow={1}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 'bold',
                color: '#FFF',
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              Credit
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            borderRight: '1px solid #C0C0C0',
            borderLeft: '1px solid #C0C0C0',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const item = memorizeData[virtualRow.index];
            const isExpand = expandedRows.find((ele) => ele.id === item?.id)?.isExpand || false;
            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: virtualRow.start,
                  left: 0,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  height: `45px`,
                  borderBottom: '1px solid #C0C0C0',
                  boxSizing: 'border-box',
                }}
              >
                <VirtualTableRow
                  item={item}
                  hideAmount={hideAmount}
                  theme={theme}
                  borderRightColor={borderRightColor}
                  currentTheme={currentTheme}
                  handleTransactionModal={handleTransactionModal}
                  isExpand={isExpand}
                  newLedgerAccountID={newLedgerAccountID}
                  toggleExpandRow={toggleExpandRow}
                  handleSelectRow={handleSelectRow}
                  Freeze={Freeze}
                  Tooltip={Tooltip}
                  MoneyFormatv3={MoneyFormatv3}
                  ActionButtons={ActionButtons}
                />
              </div>
            );
          })}
        </Box>

        {openingBalanceDiff?.balance !== 0 && (
          <Box
            sx={{
              display: 'flex',
              border: '1px solid #C0C0C0',
              height: '45px',
            }}
          >
            <Box
              sx={{
                width: { xs: '40%', sm: '60%' },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRight: '4px solid ',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#eef0f0',
              }}
            >
              <Typography variant="body1" sx={{ pl: 2, fontWeight: 'bold', color: 'red' }}>
                {openingBalanceDiff?.name}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '30%', sm: '20%' },
                borderRight: '4px solid ',
                borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#eef0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'red', pr: 2 }}>
                {openingBalanceDiff?.balance < 0 && '₹ ' + MoneyFormatv3(openingBalanceDiff?.balance * -1)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '30%', sm: '20%' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'red', pr: 2 }}>
                {openingBalanceDiff?.balance > 0 && '₹ ' + MoneyFormatv3(openingBalanceDiff?.balance)}
              </Typography>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            border: '1px solid #C0C0C0',
            height: '45px',
            backgroundColor: theme?.palette?.TmId.id.toString().includes('9') ? '#373a36' : theme?.palette?.bodyGray.tertiary,
          }}
        >
          <Box
            sx={{
              width: { xs: '40%', md: '60%' },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRight: '4px solid ',
              borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                pl: 2,
                fontWeight: 'bold',
                maxWidth: '100%',
                fontSize: '16px !important',
                whiteSpace: 'nowrap',
                overflow: 'scroll',
                scrollbarWidth: 'none', // For Firefox
                '&::-webkit-scrollbar': {
                  display: 'none', // For Chrome, Safari
                },
              }}
            >
              Total
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: '30%', md: '20%' },
              borderRight: '4px solid ',
              borderRightColor: theme?.palette?.TmId.id.toString().includes('9') ? theme.palette.primary.black : '#ffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: 12, lg: 16 },
                pr: 2,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'scroll',
                scrollbarWidth: 'none', // For Firefox
                '&::-webkit-scrollbar': {
                  display: 'none', // For Chrome, Safari
                },
              }}
            >
              ₹ {MoneyFormatv3(totalDebit || 0)}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: '30%', md: '20%' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: 12, lg: 16 },
                pr: 2,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'scroll',
                scrollbarWidth: 'none', // For Firefox
                '&::-webkit-scrollbar': {
                  display: 'none', // For Chrome, Safari
                },
              }}
            >
              ₹ {MoneyFormatv3(totalCredit || 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <LedgerModal {...ledgerModalParams} onSave={handleLedgerModal} onClose={closeLedgerModal} />

      <DeleteLedgerGroup
        open={openModal.deleteModal}
        setOpen={() => handleOpenModal('deleteModal')}
        setdeleteId={setdeleteId}
        selectedRow={selectedRow}
        setsuccessSnakbar={showSuccessSnackbar}
      />
      <AddPNFSales open={openModal.salesModal} setOpen={() => handleOpenModal('salesModal')} />
      <AddPNFPurchase open={openModal.purchaseModal} setOpen={() => handleOpenModal('purchaseModal')} />
      <AddPNFCreditNote open={openModal.creditNoteModal} setOpen={() => handleOpenModal('creditNoteModal')} />
      <AddPNFJournal open={openModal.journalModal} setOpen={() => handleOpenModal('journalModal')} />
      <AddPNFDebitNote open={openModal.debitNoteModal} setOpen={() => handleOpenModal('debitNoteModal')} />
      <AddBalanceSheetPayment open={openModal.paymentModal} setOpen={() => handleOpenModal('debitNoteModal')} groupName={groupName} />
      <AddBalanceSheetReceipt
        open={openModal.receiptModal}
        setOpen={() => handleOpenModal('receiptModal')}
        groupName={groupName}
        selectedRow={selectedRow}
      />

      {openModal.partyModal && (
        <AddNewPartyModal
          open={openModal.partyModal}
          setOpen={() => handleOpenModal('partyModal')}
          partyType={
            partyDetails?.partytypecd == 'SUPPLIER' ? 'supplier' : partyDetails?.partytypecd == 'CUSTOMER' ? 'customer' : 'Add Party'
          }
          Type={'Party'}
          setNewPartyID={(newId) => setNewPartyID(newId)}
          setPartyType={() => {}}
          partyGroupId={selectedRow.category === 'group' ? selectedRow.id : selectedRow.parent}
          page="Trial Balance"
          partyParamsID={partyDetails.partyid}
          newPartyName={selectedRow?.name}
        />
      )}
      {openModal.groupModal && (
        <CreateAndEditGroupModal
          open={openModal.groupModal}
          setOpen={() => handleOpenModal('groupModal')}
          groupId={selectedRow?.id}
          action={selectedRow?.action}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setNewLedgerResponse={setNewLedgerResponse}
          page={'Dashboard'}
          tab={'TrailBalance'}
        />
      )}
    </>
  );
}

export default memo(TrailBalanceTable);
